import prosCollectionComponentType from './prosCollectionComponentType';
import constants from './constants';
import ExcludedCategoryIdList from '../recommendation/mcom/config/ExcludedCategoryIdList';
import {
  getCategoryPageUrl,
  getProductListPageUrl,
  getRecentlyViewedItems,
} from './utils';
import { pageTypes } from './pageTypes';
import { minDetailProductThumbnail, onlyTwoPricesMinDetailTransformer } from './productTransformer';

const { SMALL, MEDIUM, LARGE } = constants.BREAK_POINT_TYPE;
const { BREAK_POINTS, MCOM_EXPERIMENTAL_NUMBER_OF_ITEMS } = constants;

export const getMCOMDefaultNumberOfItems = (
  isExpNumberOfItemsMcomOn,
  defaultNumber = 5,
) => (isExpNumberOfItemsMcomOn ? MCOM_EXPERIMENTAL_NUMBER_OF_ITEMS : defaultNumber);

export const getEmbeddedCarouselNumberOfItems = (entryPoint, isExpNumberOfItemsMcomOn) => ({
  [SMALL]: 2.5,
  [MEDIUM]: entryPoint === 'BCOM' ? 5.4 : 5.2,
  [LARGE]: entryPoint === 'BCOM' ? 5 : getMCOMDefaultNumberOfItems(isExpNumberOfItemsMcomOn),
});

export const getDefaultNumberOfItems = (entryPoint, isExpNumberOfItemsMcomOn) => ({
  [SMALL]: 2.4,
  [MEDIUM]: 5.5,
  [LARGE]: entryPoint === 'BCOM' ? 6 : getMCOMDefaultNumberOfItems(isExpNumberOfItemsMcomOn, 5.2),
});

/**
 * Retrieves the zone configuration for a pros collection based on the entry point and zone.
 * @param {string} [entryPoint='MCOM'] - The entry point value.
 * @param {string} zone - The zone value.
 * @param {Object} [killswitches] - The experimental properties.
 * @returns {Object} - The zone configuration.
 */
export const prosCollectionZoneConfigMap = (entryPoint = 'MCOM', zone, killswitches = {}) => {
  const { isExpNumberOfItemsMcomOn } = killswitches;

  const entryPointUpperCase = entryPoint.toUpperCase();
  const isBcom = entryPointUpperCase === 'BCOM';
  const recentlyViewedItems = getRecentlyViewedItems();
  const embeddedCarouselNumberOfItems = getEmbeddedCarouselNumberOfItems(entryPointUpperCase, isExpNumberOfItemsMcomOn);

  const defaultCarouselComponent = {
    componentType: prosCollectionComponentType.PROS_CAROUSEL,
    panelType: 'pros',
    numberOfItems: getDefaultNumberOfItems(entryPointUpperCase, isExpNumberOfItemsMcomOn),
    categoriesToExclude: [],
    transformer: isBcom ? minDetailProductThumbnail : undefined,
  };

  const defaultGridComponent = {
    componentType: prosCollectionComponentType.PROS_GRID,
    panelType: 'pros',
    numberOfItems: {
      [SMALL]: { row: 5, column: 2 },
      [MEDIUM]: { row: 6, column: 4 },
      [LARGE]: { row: 5, column: 3 },
    },
    transformer: isBcom ? minDetailProductThumbnail : undefined,
  };

  const zoneConfigMap = {
    BCOM: {
      ATBPage_Overlay: {
        componentType: prosCollectionComponentType.PROS_GRID,
        customHeaderType: 'h4',
        numberOfItems: {
          [SMALL]: { column: 2, row: 12 },
          [MEDIUM]: { column: 3, row: 8 },
          [LARGE]: { column: 3, row: 8 },
        },
        pageType: pageTypes.addToCartPage,
        maxProduct: 24,
        zoneLevelCustomClass: 'pros-add-to-bag-overlay',
      },
      ATBPage_ZONE_A: {
        customHeaderType: 'h4',
        pageType: pageTypes.addToCartPage,
      },
      ATBPage_ZONE_B: {
        customHeaderType: 'h4',
        pageType: pageTypes.addToCartPage,
      },
      ATRPage_Overlay: {
        componentType: prosCollectionComponentType.PROS_GRID,
        customHeaderType: 'h4',
        numberOfItems: {
          [SMALL]: { column: 2 },
          [MEDIUM]: { column: 3 },
          [LARGE]: { column: 3 },
        },
        pageType: pageTypes.addToCartPage,
      },
      BROWSE_ZONE_A: {
        pageType: pageTypes.browsePage,
      },
      CAT_UNAVAIL_ZONE_A: {
        pageType: pageTypes.categoryUnavailablePage,
      },
      CAT_ZONE_A: {
        pageType: pageTypes.categorySplashPage,
      },
      HP_ZONE_A: {
        pageType: pageTypes.homePage,
      },
      HP_ZONE_B: {
        pageType: pageTypes.homePage,
      },
      HP_ZONE_C: {
        pageType: pageTypes.homePage,
      },
      HP_ZONE_F: {
        pageType: pageTypes.homePage,
      },
      MAP_ZONE_A: {
        pageType: pageTypes.accountDashboardPage,
      },
      MLTPage_ZONE_A: {
        pageType: pageTypes.itemPage,
      },
      MLT_ZONE_A: {
        numberOfItems: embeddedCarouselNumberOfItems,
      },
      OCP_ZONE_A: {
        numberOfItems: embeddedCarouselNumberOfItems,
      },
      OC_Zone_A: {
        pageType: pageTypes.purchaseCompletePage,
      },
      OH_ZONE_A: {
        pageType: pageTypes.orderDetailsPage,
      },
      PR_ZONE_A: {
        pageType: pageTypes.purchaseHistoryPage,
      },
      PPET_ZONE_A: {
        pageType: pageTypes.purchaseTrackingPage,
      },
      PDP_UNAVAIL_ZONE_A: {
        componentType:
          window.innerWidth < BREAK_POINTS.LARGE
            ? prosCollectionComponentType.PROS_CAROUSEL
            : prosCollectionComponentType.PROS_GRID,
        extraRowGap: '8px',
        numberOfItems: {
          [LARGE]: { column: 6 },
        },
        pageType: pageTypes.unavailableItemPage,
      },
      PDP_ZONE_A: {
        componentType:
          window.innerWidth >= BREAK_POINTS.MEDIUM
            ? prosCollectionComponentType.PROS_VERTICAL
            : prosCollectionComponentType.PROS_CAROUSEL,
        customHeaderType: window.innerWidth >= BREAK_POINTS.MEDIUM ? 'h6' : 'h3',
        extraRowGap: '8px',
        numberOfItems:
          window.innerWidth >= BREAK_POINTS.MEDIUM
            ? {
              [MEDIUM]: { row: 5, column: 1 },
              [LARGE]: { row: 5, column: 1 },
            }
            : getDefaultNumberOfItems(entryPointUpperCase),
        pageType: pageTypes.itemPage,
      },
      PDP_ZONE_G: {
        componentType:
          window.innerWidth >= BREAK_POINTS.MEDIUM
            ? prosCollectionComponentType.PROS_VERTICAL
            : prosCollectionComponentType.PROS_CAROUSEL,
        customHeaderType: window.innerWidth >= BREAK_POINTS.MEDIUM ? 'h6' : 'h3',
        extraRowGap: '8px',
        numberOfItems:
          window.innerWidth >= BREAK_POINTS.MEDIUM
            ? {
              [MEDIUM]: { row: 3, column: 1 },
              [LARGE]: { row: 3, column: 1 },
            }
            : getDefaultNumberOfItems(entryPointUpperCase),
        pageType: pageTypes.itemPage,
      },
      PDP_ZONE_B: {
        pageType: pageTypes.itemPage,
      },
      PDP_ZONE_F: {
        componentType: prosCollectionComponentType.PROS_GRID,
        extraRowGap: '8px',
        numberOfItems: {
          [SMALL]: { column: 2 },
          [MEDIUM]: { column: 3 },
          [LARGE]: { column: 6 },
        },
        pageType: pageTypes.itemPage,
      },
      PDP_ZONE_E: {
        componentType: prosCollectionComponentType.PROS_BEAUTY_REGIMEN,
        pageType: pageTypes.itemPage,
      },
      REG_ZONE_A: {
        pageType: pageTypes.registryPage,
      },
      REG_ZONE_B: {
        pageType: pageTypes.registryPage,
      },
      REG_ZSR_ZONE_B: {
        pageType: pageTypes.registryZeroSearchResultsPage,
      },
      RVI_ZONE_A: {
        componentType: prosCollectionComponentType.RVI_CAROUSEL,
        panelType: constants.VIEW_TYPE.RVI,
      },
      SB_RVI: {
        componentType: prosCollectionComponentType.RVI_CAROUSEL,
        panelType: constants.VIEW_TYPE.RVI,
        numberOfItems: {
          [SMALL]: 2.5,
          [MEDIUM]: 5,
          [LARGE]: 5,
        },
      },
      SB_ZONE_A: {
        pageType: pageTypes.cartPage,
        numberOfItems: {
          [SMALL]: 2.5,
          [MEDIUM]: 5,
          [LARGE]: 5,
        },
      },
      rvi_bag_zone: {
        pageType: pageTypes.cartPage,
        numberOfItems: {
          [SMALL]: 2.5,
          [MEDIUM]: 5,
          [LARGE]: 5,
        },
      },
      SB_ZONE_EMPTY: {
        pageType: pageTypes.cartPage,
      },
      SEARCH_ZONE_A: {
        pageType: pageTypes.searchPage,
        rviDependent: true,
      },
      SPD_ZONE_A: {
        pageType: pageTypes.storePurchaseDetailsPage,
      },
      WISHLIST_EMPTY_ZONE_A: {
        pageType: pageTypes.wishListPage,
      },
      WLST_ATBPage_Overlay: {
        componentType: prosCollectionComponentType.PROS_GRID,
        numberOfItems: {
          [SMALL]: { column: 2 },
          [MEDIUM]: { column: 2 },
          [LARGE]: { column: 2 },
        },
        pageType: pageTypes.addToCartPage,
      },
      WLST_ATRPage_Overlay: {
        pageType: pageTypes.addToCartPage,
      },
      ZSR_ZONE_B: {
        componentType: prosCollectionComponentType.PROS_GRID,
        extraRowGap: '8px',
        numberOfItems: {
          [SMALL]: { column: 2 },
          [MEDIUM]: { column: 3 },
          [LARGE]: { column: 6 },
        },
        pageType: pageTypes.zeroSearchResultsPage,
      },
      HP_ZONE_G: {
        componentType: prosCollectionComponentType.PROS_DASHBOARD,
      },
    },
    MCOM: {
      ATBPage_ZONE_C: {
        componentType:
          window.innerWidth < BREAK_POINTS.SMALL
            ? prosCollectionComponentType.PROS_CAROUSEL
            : prosCollectionComponentType.PROS_GRID,
        customHeaderType: window.innerWidth > BREAK_POINTS.SMALL ? 'h4' : 'h3',
        extraRowGap: '8px',
        numberOfItems: {
          [SMALL]: 2.4,
          [MEDIUM]: { column: 5, row: 3 },
          [LARGE]: { column: 5, row: 3 },
        },
      },
      ATBPage_ZONE_D: {
        componentType:
          window.innerWidth < BREAK_POINTS.SMALL
            ? prosCollectionComponentType.PROS_CAROUSEL
            : prosCollectionComponentType.PROS_GRID,
        customHeaderType: window.innerWidth > BREAK_POINTS.SMALL ? 'h4' : 'h3',
        extraRowGap: '8px',
        numberOfItems: {
          [SMALL]: 2.4,
          [MEDIUM]: { column: 5, row: 3 },
          [LARGE]: { column: 5, row: 3 },
        },
        rviDependent: recentlyViewedItems?.length > 0,
      },
      BROWSE_ZONE_A: {
        pageType: pageTypes.browsePage,
        noDefaultRecs: recentlyViewedItems?.length > 0,
        rviDependent: recentlyViewedItems?.length > 0,
      },
      BROWSE_ZONE_B: {
        customHeaderType: 'h4',
        getLinkUrl: getCategoryPageUrl,
        linkName: 'View Gift Guide',
        numberOfItems: embeddedCarouselNumberOfItems,
        transformer: minDetailProductThumbnail,
      },
      BROWSE_ZONE_C: {
        categoriesToExclude: ExcludedCategoryIdList,
        customHeaderType: 'h4',
        numberOfItems: embeddedCarouselNumberOfItems,
        transformer: minDetailProductThumbnail,
      },
      BROWSE_ZONE_D: {
        customHeaderType: 'h4',
        getLinkUrl: getCategoryPageUrl,
        linkName: 'View Gift Guide',
        numberOfItems: embeddedCarouselNumberOfItems,
        transformer: minDetailProductThumbnail,
      },
      BROWSE_ZONE_E: {
        customHeaderType: 'h4',
        numberOfItems: embeddedCarouselNumberOfItems,
        getLinkUrl: getCategoryPageUrl,
        linkName: 'View Gift Guide',
        transformer: minDetailProductThumbnail,
      },
      BROWSE_ZONE_F: {
        customHeaderType: 'h4',
        numberOfItems: embeddedCarouselNumberOfItems,
        getLinkUrl: getCategoryPageUrl,
        linkName: 'View Gift Guide',
        transformer: minDetailProductThumbnail,
      },
      BROWSE_ZONE_G: {
        customHeaderType: 'h4',
        numberOfItems: embeddedCarouselNumberOfItems,
        getLinkUrl: getCategoryPageUrl,
        linkName: 'View Gift Guide',
        transformer: minDetailProductThumbnail,
      },
      BROWSE_ZONE_H: {
        customHeaderType: 'h4',
        numberOfItems: embeddedCarouselNumberOfItems,
        getLinkUrl: getCategoryPageUrl,
        linkName: 'View Gift Guide',
        transformer: minDetailProductThumbnail,
      },
      BROWSE_ZONE_I: {
        customHeaderType: 'h4',
        numberOfItems: embeddedCarouselNumberOfItems,
        getLinkUrl: getCategoryPageUrl,
        linkName: 'View Gift Guide',
        transformer: minDetailProductThumbnail,
      },
      BROWSE_ZONE_J: {
        customHeaderType: 'h4',
        numberOfItems: embeddedCarouselNumberOfItems,
        getLinkUrl: getCategoryPageUrl,
        linkName: 'View Gift Guide',
        transformer: minDetailProductThumbnail,
      },
      BROWSE_ZONE_K: {
        customHeaderType: 'h4',
        numberOfItems: embeddedCarouselNumberOfItems,
        transformer: minDetailProductThumbnail,
      },
      BROWSE_ZONE_L: {
        customHeaderType: 'h4',
        numberOfItems: embeddedCarouselNumberOfItems,
        getLinkUrl: getCategoryPageUrl,
        linkName: 'View Gift Guide',
        transformer: minDetailProductThumbnail,
      },
      C2FLEX_ZONE_A: {
        customHeaderType: 'h4',
        numberOfItems: {
          [SMALL]: 2.5,
          [MEDIUM]: 5,
          [LARGE]: getMCOMDefaultNumberOfItems(isExpNumberOfItemsMcomOn),
        },
        noDefaultRecs: recentlyViewedItems?.length > 0,
        rviDependent: recentlyViewedItems?.length > 0,
      },
      C2FLEX_ZONE_C: {
        componentType:
          window.innerWidth > BREAK_POINTS.SMALL
            ? prosCollectionComponentType.PROS_CONTENT_COLLECTION
            : prosCollectionComponentType.PROS_CONTENT_CAROUSEL,
        numberOfItems: {
          [SMALL]: 2.5,
          [MEDIUM]: { row: 2, column: 4 },
          [LARGE]: { row: 2, column: 3 },
        },
        linkName: 'Shop All',
        getLinkUrl: getProductListPageUrl,
        transformer: minDetailProductThumbnail,
      },
      C2FLEX_ZONE_D: {
        noDefaultRecs: recentlyViewedItems?.length > 0,
        rviDependent: recentlyViewedItems?.length > 0,
      },
      C2_RVI: {
        componentType: prosCollectionComponentType.RVI_CAROUSEL,
        panelType: 'rvi',
      },
      DISCO_RVI: {
        componentType: prosCollectionComponentType.RVI_CAROUSEL,
        panelType: 'rvi',
      },
      HP_RVI: {
        componentType: prosCollectionComponentType.RVI_CAROUSEL,
        panelType: 'rvi',
      },
      HP_ZONE_B: {
        pageType: pageTypes.homePage,
      },
      HP_ZONE_C: {
        pageType: pageTypes.homePage,
      },
      HP_ZONE_D: {
        componentType:
          window.innerWidth > BREAK_POINTS.SMALL
            ? prosCollectionComponentType.PROS_CONTENT_COLLECTION
            : prosCollectionComponentType.PROS_CONTENT_CAROUSEL,
        numberOfItems: {
          [SMALL]: 2.5,
          [MEDIUM]: { row: 2, column: 4 },
          [LARGE]: { row: 2, column: 3 },
        },
        linkName: 'Shop All',
        getLinkUrl: getProductListPageUrl,
        transformer: minDetailProductThumbnail,
      },
      HP_ZONE_E: {
        linkName: 'View All',
        getLinkUrl: getCategoryPageUrl,
      },
      HP_ZONE_K: {
        linkName: 'View All',
        getLinkUrl: getCategoryPageUrl,
      },
      HP_ZONE_L: {
        linkName: 'View All',
        getLinkUrl: getCategoryPageUrl,
      },
      HP_ZONE_M: {
        linkName: 'View All',
        getLinkUrl: getCategoryPageUrl,
      },
      MAP_ZONE_A: {
        pageType: pageTypes.accountDashboardPage,
      },
      MLT_ZONE_A: {
        componentType: prosCollectionComponentType.PROS_CAROUSEL,
        numberOfItems: embeddedCarouselNumberOfItems,
      },
      MLTPage_ZONE_A: {
        customHeaderType: 'h4',
        pageType: pageTypes.itemPage,
        numberOfItems: {
          [SMALL]: 2.5,
          [MEDIUM]: 5,
          [LARGE]: getMCOMDefaultNumberOfItems(isExpNumberOfItemsMcomOn),
        },
        headerTabIndex: 0,
        headerAriaLabel: 'You entered in the more like this area of selected product',
      },
      OCP_ZONE_A: {
        customHeaderType: 'h4',
        numberOfItems: embeddedCarouselNumberOfItems,
      },
      OH_ZONE_A: {
        pageType: pageTypes.orderDetailsPage,
      },
      RVI_PDP: {
        componentType: prosCollectionComponentType.RVI_CAROUSEL,
        panelType: 'rvi',
      },
      rvi_pdp_zone: {
        componentType: prosCollectionComponentType.RVI_CAROUSEL,
        panelType: 'rvi',
      },
      PDP_ZONE_A: {
        componentType:
          window.innerWidth >= BREAK_POINTS.MEDIUM
            ? prosCollectionComponentType.PROS_VERTICAL
            : prosCollectionComponentType.PROS_CAROUSEL,
        customHeaderType: window.innerWidth >= BREAK_POINTS.MEDIUM ? 'h6' : 'h3',
        extraRowGap: '8px',
        numberOfItems:
          window.innerWidth >= BREAK_POINTS.MEDIUM
            ? {
              [MEDIUM]: { row: 5, column: 1 },
              [LARGE]: { row: 5, column: 1 },
            }
            : getDefaultNumberOfItems(entryPointUpperCase),
        pageType: pageTypes.itemPage,
        transformer:
          window.innerWidth >= BREAK_POINTS.MEDIUM
            ? onlyTwoPricesMinDetailTransformer
            : undefined,
      },
      PDP_ZONE_G: {
        componentType:
          window.innerWidth >= BREAK_POINTS.MEDIUM
            ? prosCollectionComponentType.PROS_VERTICAL
            : prosCollectionComponentType.PROS_CAROUSEL,
        customHeaderType: window.innerWidth >= BREAK_POINTS.MEDIUM ? 'h6' : 'h3',
        extraRowGap: '8px',
        numberOfItems:
          window.innerWidth >= BREAK_POINTS.MEDIUM
            ? {
              [MEDIUM]: { row: 3, column: 1 },
              [LARGE]: { row: 3, column: 1 },
            }
            : getDefaultNumberOfItems(entryPointUpperCase),
        pageType: pageTypes.itemPage,
        transformer:
          window.innerWidth >= BREAK_POINTS.MEDIUM
            ? onlyTwoPricesMinDetailTransformer
            : undefined,
      },
      PDP_ZONE_B: {
        pageType: pageTypes.itemPage,
      },
      PDP_ZONE_F: {
        componentType: prosCollectionComponentType.PROS_GRID,
        extraRowGap: '8px',
        numberOfItems: {
          [SMALL]: { column: 2 },
          [MEDIUM]: { column: 3 },
          [LARGE]: { column: 6 },
        },
        pageType: pageTypes.itemPage,
      },
      PDP_ZONE_E: {
        componentType: prosCollectionComponentType.PROS_FBT,
        transformer: minDetailProductThumbnail,
      },
      RVI_ZONE_A: {
        componentType: prosCollectionComponentType.RVI_CAROUSEL,
        panelType: 'rvi',
      },
      SB_RVI: {
        componentType: prosCollectionComponentType.RVI_CAROUSEL,
        panelType: 'rvi',
        numberOfItems: {
          [SMALL]: 2.5,
          [MEDIUM]: 5,
          [LARGE]: getMCOMDefaultNumberOfItems(isExpNumberOfItemsMcomOn),
        },
      },
      SB_ZONE_A: {
        customHeaderType: entryPoint === 'BCOM' ? 'h3' : 'h4',
        pageType: pageTypes.cartPage,
        numberOfItems: {
          [SMALL]: 2.5,
          [MEDIUM]: 5,
          [LARGE]: getMCOMDefaultNumberOfItems(isExpNumberOfItemsMcomOn),
        },
      },
      rvi_bag_zone: {
        customHeaderType: entryPoint === 'BCOM' ? 'h3' : 'h4',
        pageType: pageTypes.cartPage,
        numberOfItems: {
          [SMALL]: 2.5,
          [MEDIUM]: 5,
          [LARGE]: getMCOMDefaultNumberOfItems(isExpNumberOfItemsMcomOn),
        },
      },
      SB_ZONE_EMPTY: {
        pageType: pageTypes.cartPage,
        rviDependent: recentlyViewedItems?.length > 0,
      },
      SEARCH_ZONE_A: {
        pageType: pageTypes.searchPage,
        rviDependent: true,
      },
      ZSR_RVI: {
        componentType: prosCollectionComponentType.RVI_CAROUSEL,
        panelType: 'rvi', // confirmation required - alternative logic might apply
      },
      ZSR_ZONE_B: {
        panelType: 'pros', // confirmation required - alternative logic might apply
        pageType: pageTypes.zeroSearchResultsPage,
      },
      HP_ZONE_F: {
        componentType: prosCollectionComponentType.PROS_DASHBOARD,
      },
    },
  };
  const zoneConfig = zoneConfigMap[entryPointUpperCase]?.[zone];

  if (zoneConfig) {
    const componentType = zoneConfig?.componentType;
    const defaultType = componentType === prosCollectionComponentType.PROS_GRID
      ? defaultGridComponent
      : defaultCarouselComponent;
    return { ...defaultType, ...zoneConfig };
  }

  return defaultCarouselComponent;
};
